import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MessageFormatter from '../MessageFormatter';

/**
 * GoalSectionCard Component
 *
 * Displays a single goal template section from goal-aligned report.
 * Expandable card with executive summary, findings, and recommendations.
 */
export default function GoalSectionCard({ goalSection, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!goalSection) {
    return null;
  }

  const {
    goal_template_name,
    goal_template_id,
    executive_summary,
    findings = [],
    recommendations = [],
    metadata = {}
  } = goalSection;

  const displayGoalName = goal_template_id === 'combined'
    ? 'AI Evaluation'
    : goal_template_name;

  // Handle case where section generation failed
  const hasFailed = !executive_summary && findings.length === 0 && recommendations.length === 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mb: 3,
        borderRadius: 2,
        '&:before': { display: 'none' },
        boxShadow: 3
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'rgb(1, 28, 45)',
          color: 'white',
          borderRadius: expanded ? '8px 8px 0 0' : '8px',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 2
          },
          '&:hover': {
            backgroundColor: 'rgb(1, 40, 60)'
          }
        }}
      >
        <Typography variant="h5">
          {displayGoalName}
        </Typography>

        {metadata.confidence_level && (
          <Chip
            label={`Confidence: ${Math.round(metadata.confidence_level * 100)}%`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ p: 4 }}>
        {hasFailed ? (
          <Alert severity="warning">
            Failed to generate insights for this goal. This may be due to insufficient data or LLM processing error.
            Try regenerating the report.
          </Alert>
        ) : (
          <Box>
            {/* Executive Summary */}
            {executive_summary && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Bebas Neue',
                    color: 'rgb(1, 28, 45)',
                    mb: 2
                  }}
                >
                  EXECUTIVE SUMMARY
                </Typography>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: 'rgba(1, 28, 45, 0.03)',
                    borderRadius: 2,
                    borderLeft: '4px solid rgb(1, 28, 45)'
                  }}
                >
                  <MessageFormatter content={executive_summary} />
                </Box>
              </Box>
            )}

            {/* Key Findings */}
            {findings.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Bebas Neue',
                    color: 'rgb(1, 28, 45)',
                    mb: 2
                  }}
                >
                  KEY FINDINGS
                </Typography>
                <List>
                  {findings.map((finding, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          <CheckCircleOutlineIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                              {finding.title || `Finding ${index + 1}`}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <MessageFormatter content={finding.description || finding.text || ''} />
                              {finding.evidence && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: 1,
                                    borderLeft: '3px solid #ccc',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Evidence:
                                  </Typography>
                                  <Typography variant="body2">
                                    "{finding.evidence}"
                                  </Typography>
                                </Box>
                              )}
                              {finding.impact && (
                                <Box sx={{ mt: 1 }}>
                                  <Chip
                                    label={`Impact: ${finding.impact}`}
                                    size="small"
                                    color={
                                      finding.impact.toLowerCase().includes('high') ? 'error' :
                                      finding.impact.toLowerCase().includes('medium') ? 'warning' :
                                      'default'
                                    }
                                  />
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < findings.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Bebas Neue',
                    color: 'rgb(1, 28, 45)',
                    mb: 2
                  }}
                >
                  RECOMMENDATIONS
                </Typography>
                <List>
                  {recommendations.map((rec, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          <LightbulbOutlinedIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {rec.title || `Recommendation ${index + 1}`}
                              </Typography>
                              {rec.priority && (
                                <Chip
                                  label={rec.priority}
                                  size="small"
                                  color={
                                    rec.priority.toLowerCase() === 'high' ? 'error' :
                                    rec.priority.toLowerCase() === 'medium' ? 'warning' :
                                    'default'
                                  }
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <MessageFormatter content={rec.description || rec.text || ''} />
                              {rec.timeframe && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Timeframe: {rec.timeframe}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recommendations.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
